import type { MessageKey } from "@/composables/useI18n";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { useRouter } from "vue-router";

type OpenParam<K extends string> = (key: K) => string;

// Safari blocks `window.open` once an `await` has run since the click, so the tab opens the
// `Open` page right away and that page resolves the URL then redirects.
export const openActions = {
  treezorKycLiveness: {
    message: "open.treezorKycLiveness",
    resolveUrl: async (param: OpenParam<"accountId">) =>
      (await call("startTreezorKycLiveness")({ accountId: param("accountId") })).url,
  },
  contract: {
    message: "open.contract",
    resolveUrl: async (param: OpenParam<"accountId">) => {
      const { url } = await call("getContractUrl")({ accountId: param("accountId") });
      if (!url) throw new Error("Contract not found");
      return url;
    },
  },
  contractSigning: {
    message: "open.contractSigning",
    resolveUrl: async (param: OpenParam<"accountId" | "invitationId">) =>
      (
        await call("getContractSigningUrl")({
          accountId: param("accountId"),
          invitationId: param("invitationId"),
        })
      ).url,
  },
  bankDetails: {
    message: "open.bankDetails",
    resolveUrl: async (param: OpenParam<"accountId">) =>
      (await call("getAccountBankDetails")({ accountId: param("accountId") })).url,
  },
  treezorAccountDocument: {
    message: "open.document",
    resolveUrl: async (param: OpenParam<"accountId" | "documentId">) =>
      (
        await call("getTreezorDocumentUrlForAccount")({
          accountId: param("accountId"),
          documentId: param("documentId"),
        })
      ).url,
  },
  treezorUserDocument: {
    message: "open.document",
    resolveUrl: async (param: OpenParam<"accountId" | "documentId" | "userId">) =>
      (
        await call("getTreezorDocumentUrlForUser")({
          accountId: param("accountId"),
          documentId: param("documentId"),
          userId: parseInt(param("userId"), 10),
        })
      ).url,
  },
  webhookDashboard: {
    message: "open.webhookDashboard",
    resolveUrl: async (param: OpenParam<"supervisorOrgId">) =>
      (await call("getWebhookDashboardUrl")({ supervisorOrgId: param("supervisorOrgId") })).url,
  },
} satisfies Record<
  string,
  { message: MessageKey; resolveUrl: (param: OpenParam<string>) => Promise<string> }
>;

export type OpenAction = keyof typeof openActions;

export const useOpenInNewTab = () => {
  const router = useRouter();
  const { showErrorToast } = useToast();

  return <A extends OpenAction>(
    action: A,
    query: Record<
      Parameters<Parameters<(typeof openActions)[A]["resolveUrl"]>[0]>[0],
      string | number
    >,
  ) => {
    const newTab = window.open(
      router.resolve({ name: "Open", params: { action }, query }).href,
      "_blank",
    );
    if (!newTab) {
      showErrorToast();
      return false;
    }
    newTab.opener = null;
    return true;
  };
};
